import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import { toast } from 'react-toastify';
import api from '../services/api';

const AdminNominations = () => {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    fetchNominations();
  }, []);

  const fetchNominations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/nominations');
      if (response.data.success) {
        setNominations(response.data.nominations);
        setStats({
          total: response.data.nominations.length,
          pending: response.data.nominations.filter(n => n.status === 'pending').length,
          approved: response.data.nominations.filter(n => n.status === 'approved').length,
          rejected: response.data.nominations.filter(n => n.status === 'rejected').length
        });
      }
    } catch (error) {
      console.error('Error fetching nominations:', error);
      toast.error('Failed to load nominations');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await api.put(`/admin/nominations/${id}`, { status });
      if (response.data.success) {
        toast.success(`Nomination ${status} successfully`);
        fetchNominations();
      }
    } catch (error) {
      console.error('Error updating nomination:', error);
      toast.error('Failed to update nomination');
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredNominations = nominations.filter(n => 
    filter === 'all' ? true : n.status === filter
  );

  const nominationsByPosition = filteredNominations.reduce((acc, nom) => {
    if (!acc[nom.position]) acc[nom.position] = [];
    acc[nom.position].push(nom);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leadership Nominations</h1>
        <p className="text-gray-600 mt-1">Review and manage alumni nominations for leadership positions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-gray-500 text-sm">Total Nominations</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-gray-500 text-sm">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-gray-500 text-sm">Approved</p>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-gray-500 text-sm">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 ${filter === 'all' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>All ({stats.total})</button>
        <button onClick={() => setFilter('pending')} className={`px-4 py-2 ${filter === 'pending' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Pending ({stats.pending})</button>
        <button onClick={() => setFilter('approved')} className={`px-4 py-2 ${filter === 'approved' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Approved ({stats.approved})</button>
        <button onClick={() => setFilter('rejected')} className={`px-4 py-2 ${filter === 'rejected' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}>Rejected ({stats.rejected})</button>
      </div>

      {Object.keys(nominationsByPosition).length === 0 ? (
        <div className="text-center py-12 text-gray-500">No nominations found</div>
      ) : (
        Object.keys(nominationsByPosition).map(position => (
          <div key={position} className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b">{position}</h2>
            <div className="space-y-4">
              {nominationsByPosition[position].map(nomination => (
                <div key={nomination.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{nomination.nominee_name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(nomination.status)}`}>{nomination.status}</span>
                      </div>
                      <p className="text-sm text-gray-600"><strong>Membership:</strong> {nomination.nominee_membership}</p>
                      <p className="text-sm text-gray-600"><strong>Department:</strong> {nomination.department_name || 'N/A'}</p>
                      <p className="text-sm text-gray-700 mt-2"><strong>Reason:</strong> {nomination.reason}</p>
                      <p className="text-xs text-gray-400 mt-2"><strong>Nominated by:</strong> {nomination.nominator_name} ({nomination.nominator_membership})</p>
                      <p className="text-xs text-gray-400">Submitted: {new Date(nomination.created_at).toLocaleString()}</p>
                    </div>
                    {nomination.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdateStatus(nomination.id, 'approved')} className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm">Approve</button>
                        <button onClick={() => handleUpdateStatus(nomination.id, 'rejected')} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AdminNominations;
